import { Component, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import { MovimentoFinanceiroMensalItem } from '../../../models/financeiro.model';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';

interface MovimentoMensalRow extends MovimentoFinanceiroMensalItem {
  label: string;
}

const NOMES_MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

@Component({
  selector: 'app-relatorio-movimento-financeiro',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './relatorio-movimento-financeiro.component.html',
  styleUrl: './relatorio-movimento-financeiro.component.css'
})
export class RelatorioMovimentoFinanceiroComponent {
  private analyticsService = inject(AnalyticsService);
  private toastr = inject(ToastrService);
  private datePipe = inject(DatePipe);

  periodoInicial = '';
  periodoFinal = '';

  movimentos: MovimentoMensalRow[] = [];
  carregado = false;
  carregando = false;
  tentouBuscar = false;
  dataGeracao = '';

  get periodoInicialInvalido(): boolean {
    return this.tentouBuscar && this.periodoInicial.trim() === '';
  }

  get periodoFinalInvalido(): boolean {
    return this.tentouBuscar && this.periodoFinal.trim() === '';
  }

  get totalFaturado(): number {
    return this.movimentos.reduce((acc, m) => acc + (m.totalFaturado ?? 0), 0);
  }

  get totalEntradas(): number {
    return this.movimentos.reduce((acc, m) => acc + (m.totalEntradas ?? 0), 0);
  }

  get totalSaidas(): number {
    return this.movimentos.reduce((acc, m) => acc + (m.totalSaidas ?? 0), 0);
  }

  get saldoTotal(): number {
    return this.movimentos.reduce((acc, m) => acc + (m.saldo ?? 0), 0);
  }

  buscar(): void {
    this.tentouBuscar = true;

    if (this.periodoInicial.trim() === '' || this.periodoFinal.trim() === '') {
      this.toastr.warning('Informe o período inicial e o período final para gerar o relatório.', 'Período obrigatório');
      return;
    }

    if (this.periodoInicial > this.periodoFinal) {
      this.toastr.warning('O período inicial deve ser anterior ou igual ao período final.', 'Período inválido');
      return;
    }

    this.carregando = true;
    this.carregado = false;

    const [anoInicial, mesInicial] = this.periodoInicial.split('-');
    const [anoFinal, mesFinal] = this.periodoFinal.split('-');

    this.analyticsService.getRelatorioMovimentoFinanceiroMensal(anoInicial, mesInicial, anoFinal, mesFinal).subscribe({
      next: (data) => {
        const labels = this.montarLabelsMeses();
        this.movimentos = (data ?? []).map((item, i) => ({
          ...item,
          label: labels[i] ?? `Mês ${i + 1}`
        }));
        this.dataGeracao = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') ?? '';
        this.carregado = true;
        this.carregando = false;
      },
      error: () => {
        this.toastr.error('Erro ao carregar relatório de movimento financeiro.', 'Erro');
        this.carregando = false;
      }
    });
  }

  imprimir(): void {
    const esc = (value: string | number | null | undefined): string =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const brl = (value: number) => (value != null ? formatarMoedaBRL(value) : 'R$ 0,00');

    const periodLabel = `${this.formatPeriodo(this.periodoInicial)} a ${this.formatPeriodo(this.periodoFinal)}`;

    const rows = this.movimentos.map((mov, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${esc(mov.label)}</td>
        <td class="right">${brl(mov.totalFaturado)}</td>
        <td class="right">${brl(mov.totalEntradas)}</td>
        <td class="right">${brl(mov.totalSaidas)}</td>
        <td class="right bold ${mov.saldo >= 0 ? 'saldo-positivo' : 'saldo-negativo'}">${brl(mov.saldo)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Movimento Financeiro Mensal</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }

    @page {
      size: A4 landscape;
      margin: 14mm 12mm 18mm 12mm;
      @bottom-left   { content: "Relatório de Movimento Financeiro Mensal"; font-size: 7.5pt; color: #555; }
      @bottom-center { content: "Página " counter(page) " de " counter(pages); font-size: 7.5pt; color: #000; }
      @bottom-right  { content: "Gerado em: ${esc(this.dataGeracao)}"; font-size: 7.5pt; color: #555; }
    }

    /* Cabeçalho */
    .header { text-align: center; margin-bottom: 10px; }
    .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 0.5px; }
    .header .meta { font-size: 9pt; color: #444; margin-top: 4px; }
    .header .divider { border: none; border-top: 2px solid #000; margin-top: 8px; }

    /* Tabela */
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { background-color: #1a1a2e; color: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    thead th { padding: 6px 7px; font-size: 9pt; font-weight: 700; text-align: left; border: 1px solid #333; }
    tbody td { padding: 4px 7px; font-size: 9pt; border: 1px solid #ccc; vertical-align: middle; }
    tbody tr.even { background-color: #f5f5f5; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    /* Totais */
    tfoot tr td { padding: 5px 7px; font-size: 9pt; font-weight: 700; border-top: 2px solid #000; border-bottom: 1px solid #ccc; background-color: #e0e0e0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Alinhamentos */
    .center { text-align: center; }
    .right  { text-align: right; }
    .bold   { font-weight: 700; }
    thead th.right  { text-align: right; }
    thead th.center { text-align: center; }

    /* Saldo */
    .saldo-positivo { color: #0a58ca; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .saldo-negativo { color: #b91c1c; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Rodapé de resumo */
    .summary { margin-top: 14px; font-size: 9pt; color: #333; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Movimento Financeiro Mensal</h1>
    <div class="meta">
      Período: ${esc(periodLabel)} &nbsp;|&nbsp; Total: ${esc(this.movimentos.length)} mês(es) &nbsp;|&nbsp; Gerado em: ${esc(this.dataGeracao)}
    </div>
    <hr class="divider"/>
  </div>

  <table>
    <thead>
      <tr>
        <th>Mês</th>
        <th class="right">Faturado</th>
        <th class="right">Entradas</th>
        <th class="right">Saídas</th>
        <th class="right">Saldo</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td class="right">Totais:</td>
        <td class="right">${brl(this.totalFaturado)}</td>
        <td class="right">${brl(this.totalEntradas)}</td>
        <td class="right">${brl(this.totalSaidas)}</td>
        <td class="right ${this.saldoTotal >= 0 ? 'saldo-positivo' : 'saldo-negativo'}">${brl(this.saldoTotal)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="summary">
    Saldo acumulado do período: <strong>${brl(this.saldoTotal)}</strong>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1100,height=750');
    if (!win) {
      this.toastr.warning('O navegador bloqueou a abertura da janela de impressão. Permita pop-ups e tente novamente.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }

  limpar(): void {
    this.periodoInicial = '';
    this.periodoFinal = '';
    this.movimentos = [];
    this.carregado = false;
    this.tentouBuscar = false;
  }

  formatPeriodo(periodo: string): string {
    if (!periodo) return '';
    const [ano, mes] = periodo.split('-');
    return `${mes}/${ano}`;
  }

  private montarLabelsMeses(): string[] {
    const [anoIni, mesIni] = this.periodoInicial.split('-').map(Number);
    const [anoFim, mesFim] = this.periodoFinal.split('-').map(Number);
    const labels: string[] = [];

    let ano = anoIni;
    let mes = mesIni;
    while (ano < anoFim || (ano === anoFim && mes <= mesFim)) {
      labels.push(`${NOMES_MESES[mes - 1]}/${ano}`);
      mes++;
      if (mes > 12) {
        mes = 1;
        ano++;
      }
    }
    return labels;
  }
}
